const validateEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};
/**
 * Valida un CUIT/CUIL argentino mediante el algoritmo de dígito verificador.
 * @param {string} cuit - El CUIT/CUIL a validar.
 * @returns {boolean} - True si el CUIT es válido, False en caso contrario.
 */
export const validateCuil = (cuil) => {
  if (!cuil) return false;

  // 1. Limpiar el cuil: Quitar guiones o espacios
  const cleanedcuil = cuil.toString().replace(/[-\s]/g, "");

  // 2. Verificar longitud (11 dígitos)
  if (cleanedcuil.length !== 11 || !/^\d{11}$/.test(cleanedcuil)) {
    return false;
  }

  // 3. Verificar prefijo válido según AFIP (20, 23, 24, 27, 30, 33, 34)
  const prefijo = cleanedcuil.substring(0, 2);
  if (!["20", "23", "24", "27", "30", "33", "34"].includes(prefijo)) {
    return false;
  }

  const DIGITO_VERIFICADOR = parseInt(cleanedcuil[10], 10);

  // 4. Algoritmo de verificación estándar AFIP (pesos)
  const factores = [5, 4, 3, 2, 7, 6, 5, 4, 3, 2];
  let suma = 0;
  for (let i = 0; i < 10; i++) {
    suma += parseInt(cleanedcuil[i], 10) * factores[i];
  }

  const resto = suma % 11;

  // 5. Calcular el dígito verificador esperado
  // Si resto === 1, el CUIL es inválido (AFIP no asigna CUILs con este resultado)
  if (resto === 1) return false;

  const digitoEsperado = resto === 0 ? 0 : 11 - resto;

  return DIGITO_VERIFICADOR === digitoEsperado;
};
export const validateForm = (formData, setErrors) => {
  const newErrors = {};
  const { datosPersonales = {}, datosLaborales = {} } = formData;

  let soloLetras = /^[a-zA-ZÁÉÍÓÚñÑ\s]*$/;

  // --- Datos Personales Validation ---
  if (!datosPersonales.cuil) {
    newErrors.cuil = "El CUIL es requerido";
  } else if (!/^\d{2}-?\d{8}-?\d{1}$/.test(datosPersonales.cuil.toString())) {
    newErrors.cuil = "El CUIL debe tener 11 dígitos";
  } else if (!validateCuil(datosPersonales.cuil)) {
    newErrors.cuil = "El CUIL ingresado es inválido";
  }

  if (!datosPersonales.nro_doc) {
    newErrors.nro_doc = "El Numero de Documento es requerido";
  }

  // Cross-check CUIL and DNI
  if (
    !newErrors.cuil &&
    !newErrors.nro_doc &&
    datosPersonales.cuil &&
    datosPersonales.nro_doc
  ) {
    const cuilLimpio = datosPersonales.cuil.toString().replace(/-/g, "");
    const dniExtraidoDeCuil = cuilLimpio.substring(2, 10);
    const dniLimpio = datosPersonales.nro_doc.toString().replace(/[^0-9]/g, "");
    if (parseInt(dniExtraidoDeCuil, 10) !== parseInt(dniLimpio, 10)) {
      newErrors.nro_doc =
        "El DNI no coincide con la parte central del CUIL/CUIT";
    }
  }

  if (!datosPersonales.nro_legajo) {
    newErrors.nro_legajo = "El número de legajo es requerido";
  } else if (!/^\d+$/.test(datosPersonales.nro_legajo)) {
    newErrors.nro_legajo = "Solo se permiten números";
  } else if (datosPersonales.nro_legajo.length > 5) {
    newErrors.nro_legajo = "No debe tener más de 5 dígitos";
  }

  if (!datosPersonales.nombre) {
    newErrors.nombre = "El nombre es requerido";
  } else if (datosPersonales.nombre.length < 2) {
    newErrors.nombre = "Mínimo 2 caracteres";
  } else if (!soloLetras.test(datosPersonales.nombre)) {
    newErrors.nombre = "Solo se permiten letras";
  }

  if (!datosPersonales.apellido) {
    newErrors.apellido = "El apellido es requerido";
  } else if (datosPersonales.apellido.length < 2) {
    newErrors.apellido = "Mínimo 2 caracteres";
  } else if (!soloLetras.test(datosPersonales.apellido)) {
    newErrors.apellido = "Solo se permiten letras";
  }


  // --- Validación de Teléfonos (Array) ---
  const telefonos = formData.telefonosEmpleado || [];

  // Validar que haya al menos un teléfono
  if (!telefonos || telefonos.length === 0) {
    newErrors.telefonosEmpleado = "Debe agregar al menos un teléfono";
  } else {
    // Validar cada teléfono del array
    telefonos.forEach((tel, index) => {
      // Validar que el tipo esté seleccionado
      if (!tel.tipo || tel.tipo === "") {
        if (!newErrors.telefonosEmpleado) newErrors.telefonosEmpleado = {};
        newErrors.telefonosEmpleado[`tipo_${index}`] = "El tipo es requerido";
      }

      // Validar el número
      if (!tel.numero || tel.numero === "") {
        if (!newErrors.telefonosEmpleado) newErrors.telefonosEmpleado = {};
        newErrors.telefonosEmpleado[`numero_${index}`] = "El número es requerido";
      } else if (!/^\d{10}$/.test(tel.numero)) {
        if (!newErrors.telefonosEmpleado) newErrors.telefonosEmpleado = {};
        newErrors.telefonosEmpleado[`numero_${index}`] = "Debe tener 10 dígitos";
      }
    });
  }

  if (!datosPersonales.email) {
    newErrors.email = "El correo electrónico es requerido";
  } else if (!validateEmail(datosPersonales.email)) {
    newErrors.email = "Formato de email inválido";
  }

  if (!datosPersonales.fecha_ingreso) {
    newErrors.fecha_ingreso = "La fecha de ingreso es requerida";
  } else {
    const fechaIngreso = new Date(datosPersonales.fecha_ingreso);
    const fechaActual = new Date();
    if (fechaIngreso > fechaActual) {
      newErrors.fecha_ingreso =
        "La fecha de ingreso no puede ser mayor a la fecha actual";
    }
    if (datosPersonales.fecha_nacimiento) {
      const fechaNacimiento = new Date(datosPersonales.fecha_nacimiento);
      if (fechaIngreso < fechaNacimiento) {
        newErrors.fecha_ingreso =
          "La fecha de ingreso no puede ser menor que la fecha de nacimiento";
      }
    }
  }

  if (datosPersonales.fecha_nacimiento) {
    const fechaNacimiento = new Date(datosPersonales.fecha_nacimiento);
    const hoy = new Date();
    const edadMinima = new Date(
      hoy.getFullYear() - 18,
      hoy.getMonth(),
      hoy.getDate()
    );
    if (fechaNacimiento > edadMinima) {
      newErrors.fecha_nacimiento = "El empleado debe tener al menos 18 años";
    }
  }

  if (datosPersonales.fecha_baja && datosPersonales.fecha_ingreso) {
    if (
      new Date(datosPersonales.fecha_baja) <=
      new Date(datosPersonales.fecha_ingreso)
    ) {
      newErrors.fecha_baja = "Debe ser posterior a la fecha de ingreso";
    }
  }

  if (!datosPersonales.id_tipo_doc) {
    newErrors.id_tipo_doc = "Debe seleccionar un tipo de documento";
  }
  if (!datosPersonales.id_provincia) {
    newErrors.id_provincia = "Debe seleccionar una provincia";
  }
  if (!datosPersonales.id_localidad) {
    newErrors.id_localidad = "Debe seleccionar una localidad";
  }
  if (datosPersonales.patente_automovil) {
    if (!/^[a-zA-Z0-9]{5,20}$/.test(datosPersonales.patente_automovil)) {
      newErrors.patente_automovil = "La patente debe tener entre 5 y 20 caracteres";
    }
  }

  // --- Datos Laborales Validation ---
  if (!datosLaborales.sucursal_id) {
    newErrors.sucursal_id = "La sucursal es requerida";
  }
  if (!datosLaborales.puesto_id) {
    newErrors.puesto_id = "El puesto es requerido";
  }
  if (!datosLaborales.sector_id) {
    newErrors.sector_id = "El sector es requerido";
  }
  if (datosLaborales.cbu) {
    if (!/^[0-9]{22}$/.test(datosLaborales.cbu)) {
      newErrors.cbu = "El CBU debe tener 22 dígitos";
    }
  }
  if (datosLaborales.alias) {
    // Permitir letras, números y puntos (común en alias de CBU) y ajustar a 8-30
    if (!/^[a-zA-Z0-9.]{8,30}$/.test(datosLaborales.alias)) {
      newErrors.alias = "El alias debe tener entre 8 y 30 caracteres (letras, números y puntos)";
    }
  }

  // --- Validación de Fichada Móvil (Geolocalización) ---
  if (datosPersonales.xdistancia === 2) {
    if (!datosPersonales.latitud || !datosPersonales.longitud) {
      newErrors.xdistancia = "Debe marcar la ubicación en el mapa para la Fichada Móvil";
    }
  }

  // --- Validación de Familiares (Array) ---
  const familiares = formData.familiares || [];

  if (familiares.length > 0) {
    familiares.forEach((familiar, index) => {
      if (!familiar.nombre?.trim()) {
        if (!newErrors.familiares) newErrors.familiares = {};
        newErrors.familiares[`nombre_${index}`] = "El nombre es requerido";
      }
      if (!familiar.apellido?.trim()) {
        if (!newErrors.familiares) newErrors.familiares = {};
        newErrors.familiares[`apellido_${index}`] = "El apellido es requerido";
      }
      if (!familiar.parentesco_id) {
        if (!newErrors.familiares) newErrors.familiares = {};
        newErrors.familiares[`parentesco_id_${index}`] = "El parentesco es requerido";
      }
      if (!familiar.id_tipo_doc) {
        if (!newErrors.familiares) newErrors.familiares = {};
        newErrors.familiares[`id_tipo_doc_${index}`] = "El tipo de documento es requerido";
      }
      if (!familiar.nro_doc) {
        if (!newErrors.familiares) newErrors.familiares = {};
        newErrors.familiares[`nro_doc_${index}`] = "El número de documento es requerido";
      }
      if (!familiar.telefono) {
        if (!newErrors.familiares) newErrors.familiares = {};
        newErrors.familiares[`telefono_${index}`] = "El teléfono es requerido";
      } else if (!/^\d{10}$/.test(familiar.telefono)) {
        if (!newErrors.familiares) newErrors.familiares = {};
        newErrors.familiares[`telefono_${index}`] = "Debe tener 10 dígitos";
      }
      if (!familiar.fecha_nacimiento) {
        if (!newErrors.familiares) newErrors.familiares = {};
        newErrors.familiares[`fecha_nacimiento_${index}`] = "La fecha de nacimiento es requerida";
      } else {
        const today = new Date().getTime();
        if (new Date(familiar.fecha_nacimiento)?.getTime() > today) {
          if (!newErrors.familiares) newErrors.familiares = {};
          newErrors.familiares[`fecha_nacimiento_${index}`] = "La fecha no puede ser futura";
        }
      }
      if (!familiar.estudio_id) {
        if (!newErrors.familiares) newErrors.familiares = {};
        newErrors.familiares[`estudio_id_${index}`] = "El estudio es requerido";
      }
    });
  }


  if (setErrors) setErrors(newErrors);
  return Object.keys(newErrors).length === 0;
};
